import React, {
  lazy, Suspense, useState, useEffect, useRef,
} from 'react';

import {
  matchRoutes,
  Navigate,
  Outlet,
  useLocation,
  useParams,
  useSearchParams,
  type RouteObject,
} from 'react-router';

import { SESSION_EXPIRED_ERROR } from '@/assets/AppConstants';

import { useAppContext } from '@/contexts/app';
import { useUserContext } from '@/contexts/user';
import { useAbilityContext } from '@/contexts/ability';
import { useWorkspaceContext } from '@/contexts/workspace';
import { useKeycloakContext } from '@/contexts/keycloak/context';

import { prefetchCategories } from '@/services/Categories';

import { getHomeFromRole } from '@/utils/routes';
import { firstEnabledPath } from '@/utils/landing';
import { APP_ROUTES } from '@/assets/MenuItems';
import { useBlockedModule } from '@/features/blocked-modules/useBlockedModule';

import Forbidden from '@/views/base/Forbidden';
import { ErrorBoundary } from '@/modern/shell';

import { LoadingLayout } from '../Loading';
import { LoadingPageNew } from '@/features/login/LoadingPageNew';
import { loginConfig } from '@/features/login/loginConfig';

const WhatsNewIntro = lazy(() => import('@/features/whats-new-intro/WhatsNewIntro'));

const LOADING_EXIT_MS = 200;

export const AuthenticatedLayout = () => {
  const { workspace = 0 } = useParams();

  const location = useLocation();
  const [searchParams] = useSearchParams();

  const ability = useAbilityContext();
  const { requestStatus } = useAppContext();
  const { cleanKeycloak } = useKeycloakContext();
  const { userIsLogged } = useUserContext();
  const { workspace: { getWorkspaceRole, workspaces, defaultWorkspace } } = useWorkspaceContext();
  const { isFeatureEnabled: blockedModulesActive } = useBlockedModule();

  const hasRole = !!getWorkspaceRole();
  const urlWorkspace = workspaces?.[Number(workspace)];
  const isScopeStale = !!urlWorkspace && String(urlWorkspace.id) !== String(defaultWorkspace?.id);
  const [loadingLeaving, setLoadingLeaving] = useState(false);
  const [loadingMounted, setLoadingMounted] = useState(!hasRole);
  const prevHasRole = useRef<any>(hasRole);

  const exitTimerRef = useRef<any>(null);
  const landedFromRef = useRef<string | null>(null);

  useEffect(() => {
    if (hasRole && !prevHasRole.current && loginConfig.useNewLoadingPage) {
      setLoadingLeaving(true);
      exitTimerRef.current = setTimeout(() => setLoadingMounted(false), LOADING_EXIT_MS);
    }
    prevHasRole.current = hasRole;
    return () => clearTimeout(exitTimerRef.current);
  }, [hasRole]);

  // Warm the category cache as soon as the workspace is resolved (hasRole means the X-Workspace-Id header
  // is already set on the axios instance). The "add category" modals block on this list, and on the largest
  // workspace it is a ~12s request — paying for it in the background beats paying for it on the first click.
  // Fire-and-forget: prefetchCategories swallows its own errors.
  useEffect(() => {
    if (hasRole) prefetchCategories();
  }, [hasRole, workspace]);

  const isSessionExpired = requestStatus === SESSION_EXPIRED_ERROR;

  useEffect(() => {
    if (isSessionExpired) {
      cleanKeycloak();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSessionExpired]);

  const finalLocation = location.pathname?.includes('logout')
    ? ''
    : `${location.pathname}${location.search}`;

  if (!userIsLogged()) {
    const redirectTo = `/login?callbackUrl=${encodeURIComponent(
      finalLocation,
    )}`;
    return <Navigate to={redirectTo} replace />;
  }

  if (isSessionExpired) {
    const redirectTo = `/login?callbackUrl=${encodeURIComponent(
      finalLocation,
    )}`;
    return <Navigate to={redirectTo} replace />;
  }

  // /ws/:workspaceId/* is owned by WorkspaceIdRedirect (it resolves the workspace id to the per-user
  // index and redirects). It must render before hasRole / menu-match checks — otherwise we get stuck
  // on the loading screen or redirect to /:workspace=0.
  if (location.pathname.startsWith('/ws/')) {
    return <Outlet />;
  }

  if (!hasRole || isScopeStale) {
    if (!loginConfig.useNewLoadingPage) return <LoadingLayout />;

    return <LoadingPageNew />;
  }

  const menuMatchRoutes = matchRoutes<RouteObject & Record<string, any>>(APP_ROUTES, location);

  if (!menuMatchRoutes) {
    const callbackUrl = searchParams.get('callbackUrl')
      || `/${workspace}${getHomeFromRole(getWorkspaceRole())}`
      || '/';

    return (
      <Navigate
        to={callbackUrl}
        replace
      />
    );
  }

  const { length, [length - 1]: routeItem } = menuMatchRoutes;
  const {
    params: { taskId: paramsTaskId },
    route: { key },
  } = routeItem;

  const keyId = paramsTaskId || key;
  const canView = ability.can('view', keyId);
  const isEnabled = ability.can('enabled', keyId);

  const landingPath = firstEnabledPath(APP_ROUTES[0].children, ability);
  const landingUrl = `/${workspace}${landingPath ?? getHomeFromRole(getWorkspaceRole())}`;
  const currentPath = location.pathname.replace(/\/$/, '');
  const isOnLanding = currentPath === landingUrl;
  const cameBackFromLanding = landedFromRef.current === currentPath;

  if (!canView && !isOnLanding && !cameBackFromLanding) {
    landedFromRef.current = currentPath;
    return <Navigate to={landingUrl} replace />;
  }

  // Quando a feature de módulos bloqueados está ligada (env + país suportado),
  // deixamos a rota seguir mesmo sem `enabled` — o LockedRouteGuard dentro do
  // layout interno renderiza o UpSellFeaturePromotion em vez do Forbidden.
  const allowRenderWhenDisabled = blockedModulesActive;

  if (!isEnabled && !allowRenderWhenDisabled && landingPath && !isOnLanding && !cameBackFromLanding) {
    landedFromRef.current = currentPath;
    return <Navigate to={landingUrl} replace />;
  }

  return (
    <>
      {loadingMounted && loginConfig.useNewLoadingPage && (
        <LoadingPageNew leaving={loadingLeaving} />
      )}
      {canView && (isEnabled || allowRenderWhenDisabled) && (
        <ErrorBoundary>
          <Suspense fallback={null}>
            <Outlet />
          </Suspense>
        </ErrorBoundary>
      )}
      {(!canView || (!isEnabled && !allowRenderWhenDisabled)) && <Forbidden />}
      <Suspense fallback={null}>
        <WhatsNewIntro />
      </Suspense>
    </>
  );
};
